import classnames from 'classnames';
import { Toolbar } from '@leverege/ui-plugin';
import { Config } from '@leverege/plugin';
import { Content, Flex, Pane } from '@leverege/ui-elements';
import { ItemCard } from '@leverege/ui-attributes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Selection } from '@leverege/ui-redux';
import { NoDataView } from '@leverege/molten/data-viewers';
import { connect } from 'react-redux';
import { LoadingScreen } from '@leverege/molten/screens';
import RsrcThumbnailRendererFactory from "../RsrcThumbnailRendererFactory.js";
import { useRsrcImage } from "./hooks/index.js";
import SelectedDialog from "./SelectedDialog.js";
import Pagination from "../pagination/Pagination.js";
import S from './Gallery.css';
import useThumbnailSelection from "./hooks/useThumbnailSelection.js";
import usePaginationControls from "./hooks/usePaginationControls.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const TYPE = 'rsrc.gallery.RsrcThumbnailRenderer';
function GalleryItem(props) {
  const {
    item,
    onSelect,
    settings,
    delegate,
    overrideModel
  } = props;
  const {
    src,
    loading,
    ref,
    inView,
    image
  } = useRsrcImage({
    item,
    delegate
  });
  const matchContext = useMemo(() => {
    return {
      client: TYPE,
      objectType: delegate.relationship.objectType,
      path: delegate.relationship.path
    };
  }, [delegate.relationship]);
  const context = useMemo(() => {
    return {
      clientProps: {
        ...props,
        thumbnail: settings?.thumbnail
      },
      target: [item],
      src
    };
  }, [props, item, src, settings?.thumbnail]);
  const onClick = evt => onSelect({
    data: item,
    original: evt
  });
  return /*#__PURE__*/_jsxs(Pane, {
    variant: "galleryItem",
    domRef: ref,
    onClick: onClick,
    className: S.galleryItem,
    children: [/*#__PURE__*/_jsx(Toolbar, {
      className: classnames(S.toolbar, S.toolbarLeft),
      variant: "rsrcActionBar|actionBar",
      prefer: "icon",
      matchContext: {
        ...matchContext,
        use: 'actionBarLeft'
      },
      context: context
    }), /*#__PURE__*/_jsx(Toolbar, {
      className: classnames(S.toolbar, S.toolbarRight),
      variant: "rsrcActionBar|actionBar",
      prefer: "icon",
      hasContextMenu: true,
      contextMenuHideWhenEmpty: true,
      matchContext: {
        ...matchContext,
        use: 'actionBar'
      },
      context: context
    }), /*#__PURE__*/_jsxs(Flex, {
      className: S.container,
      variant: "colCenterStretch",
      children: [inView ? RsrcThumbnailRendererFactory.create(settings?.thumbnail, {
        item,
        context,
        src,
        loading,
        image,
        inView,
        path: delegate.relationship.path,
        overrideModel
      }) : /*#__PURE__*/_jsx("div", {}), /*#__PURE__*/_jsx(Pane, {
        className: S.details,
        layout: "flex:rowSpaceBetweenCenter",
        children: item && /*#__PURE__*/_jsx(ItemCard, {
          model: settings?.details,
          context: {
            id: item.id,
            target: [item]
          }
        })
      })]
    })]
  });
}
const CLIENT = 'RsrcGallery';
function Gallery(props) {
  const {
    paginator,
    settings,
    selectionKey,
    delegate,
    inCard,
    relationship: {
      path
    },
    reloadData,
    loading
  } = props;
  const {
    selectedItem,
    onSelect,
    onClose
  } = useThumbnailSelection({
    selectionKey,
    delegate
  });
  const actionBarMatchContext = useMemo(() => {
    return {
      client: CLIENT,
      use: 'actionBar',
      objectType: 'rsrc',
      path
    };
  }, [path]);
  const context = useMemo(() => {
    return {
      clientProps: props
    };
  }, [props]);

  // useEffect( () => {
  //   if ( selectedItem?.data?.path ) {
  //     const search = new URLSearchParams( location.search )
  //     if ( selectedItem.data.path !== search.get( 'selectedItem' ) ) {
  //       search.set( 'selectedItem', selectedItem.data.path )
  //       GlobalState.dispatch( History.push( { search : search.toString() } ) )
  //     }
  //   }
  // }, [ selectedItem?.data?.path, location ] )

  const {
    selectedIndex,
    onNextItem,
    onPreviousItem,
    hasCycled,
    cannotCycle
  } = usePaginationControls({
    paginator,
    onSelect,
    selectedItem
  });
  const headerContent = /*#__PURE__*/_jsx(Flex, {
    className: inCard ? undefined : S.toolbars,
    variant: "rsrcGalleryToolbar|rsrcToolbar",
    children: /*#__PURE__*/_jsx(Toolbar, {
      className: S.toolbar,
      variant: "actionBar",
      prefer: "icon",
      matchContext: actionBarMatchContext,
      context: context
    })
  });
  return /*#__PURE__*/_jsxs(Content, {
    children: [/*#__PURE__*/_jsx(Content.Header, {
      children: headerContent
    }), /*#__PURE__*/_jsx(Content.Area, {
      children: loading ? /*#__PURE__*/_jsx(LoadingScreen, {}) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [selectedItem && /*#__PURE__*/_jsx(SelectedDialog, {
          ...props,
          selectedIndex: selectedIndex,
          path: path,
          show: selectedItem,
          onClose: onClose,
          settings: settings,
          selectionKey: selectionKey,
          paginator: paginator,
          delegate: delegate,
          reloadData: reloadData,
          onNextItem: onNextItem,
          onPreviousItem: onPreviousItem,
          hasCycled: hasCycled,
          cannotCycle: cannotCycle
        }), /*#__PURE__*/_jsx(GalleryContent, {
          ...props,
          paginator: paginator,
          settings: settings,
          onSelect: onSelect
        })]
      })
    }), /*#__PURE__*/_jsx(Content.Footer, {
      children: /*#__PURE__*/_jsx(Pagination, {
        paginator: paginator,
        context: context
      })
    })]
  });
}
Gallery.CLIENT = CLIENT;
function GalleryContent(props) {
  const {
    paginator,
    settings,
    onSelect
  } = props;
  let noDataMessage = Config.get(CLIENT, 'noDataMessage', NoDataView);
  if (noDataMessage != null && typeof noDataMessage !== 'string') {
    // assume its a component class/function
    const Clazz = noDataMessage;
    noDataMessage = /*#__PURE__*/_jsx(Clazz, {
      ...props,
      client: CLIENT
    });
  }
  const hasData = paginator.pageItems?.length > 0;
  return /*#__PURE__*/_jsx(Pane, {
    className: classnames(hasData ? S.gallery : S.galleryNoData),
    children: hasData > 0 ? paginator.pageItems?.map(item => item ? /*#__PURE__*/_jsx(GalleryItem, {
      ...props,
      item: item,
      settings: settings,
      onSelect: onSelect
    }, item?.id) : null) : noDataMessage
  });
}
export default connect((state, props) => {
  const {
    rolloverKey,
    selectionKey,
    targetKey
  } = props;
  return {
    targeted: Selection.get(state, targetKey),
    selected: Selection.get(state, selectionKey),
    rollover: Selection.get(state, rolloverKey),
    location: state.router.location
  };
})(Gallery);